import { useEffect, useRef, useState } from "react";
import {
  ImagePlus,
  LoaderCircle,
  Plus,
  Trash2,
  X,
} from "lucide-react";

const EMPTY_VARIANT = {
  id: null,
  name: "",
  quantity: "1",
  price: "",
  is_active: true,
  is_available: true,
  display_order: "0",
};

function createClientId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `flavor-${Date.now()}-${Math.random()}`;
}

const EMPTY_FLAVOR = {
  id: null,
  client_id: null,
  name: "",
  description: "",
  extra_price: "0.00",
  price_mode: "flat",
  is_seasonal: false,
  available_from: "",
  available_until: "",
  is_active: true,
  is_available: true,
  display_order: "0",
};

const EMPTY_ADD_ON = {
  id: null,
  client_id: null,
  name: "",
  description: "",
  extra_price: "0.00",
  price_mode: "flat",
  is_active: true,
  is_available: true,
  display_order: "0",
};

const EMPTY_FORM = {
  name: "",
  category: "",
  short_description: "",
  description: "",
  display_order: "0",
  is_active: true,
  is_featured: false,
  is_available: true,
  requires_flavor: false,
  allows_custom_flavor: false,
  custom_flavor_prompt:
    "Describe the flavor you would like.",
  image: null,
  variants: [{ ...EMPTY_VARIANT }],
  flavors: [],
  add_ons: [],
};

function createEmptyFlavor() {
  return {
    ...EMPTY_FLAVOR,
    client_id: createClientId(),
  };
}

function MenuItemForm({
  item,
  categories,
  isOpen,
  isSaving,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  const fileInputRef = useRef(null);
  const newFlavorInputRef = useRef(null);
  const isEditing = Boolean(item?.id);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (item) {
      setFormData({
        name: item.name || "",
        category: String(item.category || ""),
        short_description: item.short_description || "",
        description: item.description || "",
        display_order: String(item.display_order ?? 0),
        is_active: Boolean(item.is_active),
        is_featured: Boolean(item.is_featured),
        is_available: Boolean(item.is_available),
        requires_flavor: Boolean(item.requires_flavor),
        allows_custom_flavor: Boolean(
          item.allows_custom_flavor,
        ),
        custom_flavor_prompt:
          item.custom_flavor_prompt ||
          "Describe the flavor you would like.",
        image: null,
        variants:
          item.variants?.length > 0
            ? item.variants.map((variant) => ({
                id: variant.id ?? null,
                name: variant.name || "",
                quantity: String(variant.quantity ?? 1),
                price: String(variant.price ?? ""),
                is_active: Boolean(variant.is_active),
                is_available: Boolean(
                  variant.is_available,
                ),
                display_order: String(
                  variant.display_order ?? 0,
                ),
              }))
            : [{ ...EMPTY_VARIANT }],
        flavors:
          item.flavors?.map((flavor) => ({
            id: flavor.id ?? null,
            client_id: null,
            name: flavor.name || "",
            description: flavor.description || "",
            extra_price: String(
              flavor.extra_price ?? "0.00",
            ),
            price_mode: flavor.price_mode || "flat",
            is_seasonal: Boolean(flavor.is_seasonal),
            available_from: flavor.available_from || "",
            available_until:
              flavor.available_until || "",
            is_active: Boolean(flavor.is_active),
            is_available: Boolean(
              flavor.is_available,
            ),
            display_order: String(
              flavor.display_order ?? 0,
            ),
          })) || [],
        add_ons:
          item.add_ons?.map((addOn) => ({
            id: addOn.id ?? null,
            name: addOn.name || "",
            description: addOn.description || "",
            extra_price: String(
              addOn.extra_price ?? "0.00",
            ),
            price_mode: addOn.price_mode || "flat",
            is_active: Boolean(addOn.is_active),
            is_available: Boolean(
              addOn.is_available,
            ),
            display_order: String(
              addOn.display_order ?? 0,
            ),
          })) || [],
      });

      setImagePreview(item.image_url || null);
    } else {
      setFormData({
        ...EMPTY_FORM,
        variants: [{ ...EMPTY_VARIANT }],
        flavors: [],
        add_ons: [],
      });
      setImagePreview(null);
    }

    setErrors({});
  }, [item, isOpen]);

  useEffect(() => {
    return () => {
      if (
        imagePreview &&
        imagePreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  function handleChange(event) {
    const { name, value, type, checked } = event.target;

    setFormData((current) => {
      const next = {
        ...current,
        [name]: type === "checkbox" ? checked : value,
      };

      if (
        name === "requires_flavor" &&
        type === "checkbox" &&
        !checked
      ) {
        next.allows_custom_flavor = false;
      }

      return next;
    });

    clearFieldError(name);
  }

  function clearFieldError(name) {
    if (!errors[name]) {
      return;
    }

    setErrors((current) => ({
      ...current,
      [name]: null,
    }));
  }

  function handleImageChange(event) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setErrors((current) => ({
        ...current,
        image: "Please choose a valid image.",
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((current) => ({
        ...current,
        image: "Images must be smaller than 5 MB.",
      }));
      return;
    }

    if (
      imagePreview &&
      imagePreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(imagePreview);
    }

    setFormData((current) => ({
      ...current,
      image: file,
    }));

    setImagePreview(URL.createObjectURL(file));

    setErrors((current) => ({
      ...current,
      image: null,
    }));
  }

  function updateVariant(index, field, value) {
    setFormData((current) => ({
      ...current,
      variants: current.variants.map((variant, i) =>
        i === index
          ? {
              ...variant,
              [field]: value,
            }
          : variant,
      ),
    }));
  }

  function addVariant() {
    setFormData((current) => ({
      ...current,
      variants: [
        ...current.variants,
        {
          ...EMPTY_VARIANT,
          display_order: String(
            current.variants.length,
          ),
        },
      ],
    }));
  }

  function removeVariant(index) {
    setFormData((current) => ({
      ...current,
      variants: current.variants.filter(
        (_, i) => i !== index,
      ),
    }));
  }

  function updateFlavor(index, field, value) {
    setFormData((current) => ({
      ...current,
      flavors: current.flavors.map((flavor, i) =>
        i === index
          ? {
              ...flavor,
              [field]: value,
            }
          : flavor,
      ),
    }));
  }

  function addFlavor() {
    setFormData((current) => ({
      ...current,
      flavors: [
        createEmptyFlavor(),
        ...current.flavors,
      ],
    }));

    requestAnimationFrame(() => {
      newFlavorInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      newFlavorInputRef.current?.focus();
    });
  }

  function removeFlavor(index) {
    setFormData((current) => ({
      ...current,
      flavors: current.flavors.filter(
        (_, i) => i !== index,
      ),
    }));
  }

  function updateAddOn(index, field, value) {
    setFormData((current) => ({
      ...current,
      add_ons: current.add_ons.map((addOn, i) =>
        i === index
          ? {
              ...addOn,
              [field]: value,
            }
          : addOn,
      ),
    }));
  }

  function addAddOn() {
    setFormData((current) => ({
      ...current,
      add_ons: [
        ...current.add_ons,
        {
          ...EMPTY_ADD_ON,
          display_order: String(
            current.add_ons.length,
          ),
        },
      ],
    }));
  }

  function removeAddOn(index) {
    setFormData((current) => ({
      ...current,
      add_ons: current.add_ons.filter(
        (_, i) => i !== index,
      ),
    }));
  }

  function validateForm() {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = "Enter an item name.";
    }

    if (!formData.category) {
      nextErrors.category = "Choose a category.";
    }

    if (formData.variants.length === 0) {
      nextErrors.variants =
        "Add at least one size or pricing option.";
    }

    formData.variants.forEach((variant, index) => {
      if (!variant.name.trim()) {
        nextErrors[`variant_${index}_name`] =
          "Enter a variant name.";
      }

      const price = Number(variant.price);

      if (
        variant.price === "" ||
        Number.isNaN(price) ||
        price < 0
      ) {
        nextErrors[`variant_${index}_price`] =
          "Enter a valid price.";
      }

      const quantity = Number(variant.quantity);

      if (
        Number.isNaN(quantity) ||
        quantity < 1
      ) {
        nextErrors[`variant_${index}_quantity`] =
          "Quantity must be at least 1.";
      }
    });

    if (
      formData.requires_flavor &&
      !formData.allows_custom_flavor &&
      formData.flavors.length === 0
    ) {
      nextErrors.flavors =
        "Add at least one flavor or allow custom flavors.";
    }

    formData.flavors.forEach((flavor, index) => {
      if (!flavor.name.trim()) {
        nextErrors[`flavor_${index}_name`] =
          "Enter a flavor name.";
      }

      const extraPrice = Number(flavor.extra_price);

      if (
        Number.isNaN(extraPrice) ||
        extraPrice < 0
      ) {
        nextErrors[`flavor_${index}_extra_price`] =
          "Enter a valid extra price.";
      }

      if (
        flavor.is_seasonal &&
        flavor.available_from &&
        flavor.available_until &&
        flavor.available_until <
          flavor.available_from
      ) {
        nextErrors[`flavor_${index}_available_until`] =
          "End date cannot be before start date.";
      }
    });

    if (
      formData.allows_custom_flavor &&
      !formData.custom_flavor_prompt.trim()
    ) {
      nextErrors.custom_flavor_prompt =
        "Add instructions for the custom flavor field.";
    }

    formData.add_ons.forEach((addOn, index) => {
      if (!addOn.name.trim()) {
        nextErrors[`add_on_${index}_name`] =
          "Enter an add-on name.";
      }

      const extraPrice = Number(addOn.extra_price);

      if (
        Number.isNaN(extraPrice) ||
        extraPrice < 0
      ) {
        nextErrors[`add_on_${index}_extra_price`] =
          "Enter a valid extra price.";
      }
    });

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = new FormData();

    payload.append("name", formData.name.trim());
    payload.append("category", formData.category);
    payload.append(
      "short_description",
      formData.short_description.trim(),
    );
    payload.append(
      "description",
      formData.description.trim(),
    );
    payload.append(
      "display_order",
      formData.display_order || "0",
    );
    payload.append(
      "is_active",
      String(formData.is_active),
    );
    payload.append(
      "is_featured",
      String(formData.is_featured),
    );
    payload.append(
      "is_available",
      String(formData.is_available),
    );
    payload.append(
      "requires_flavor",
      String(formData.requires_flavor),
    );
    payload.append(
      "allows_custom_flavor",
      String(formData.allows_custom_flavor),
    );
    payload.append(
      "custom_flavor_prompt",
      formData.custom_flavor_prompt.trim(),
    );

    if (formData.image) {
      payload.append("image", formData.image);
    }

    const variants = formData.variants.map((variant) => ({
      ...(variant.id ? { id: variant.id } : {}),
      name: variant.name.trim(),
      quantity: Number(variant.quantity),
      price: Number(variant.price).toFixed(2),
      is_active: variant.is_active,
      is_available: variant.is_available,
      display_order: Number(
        variant.display_order || 0,
      ),
    }));

    const flavors = formData.flavors.map((flavor, index) => ({
      ...(flavor.id ? { id: flavor.id } : {}),
      name: flavor.name.trim(),
      description: flavor.description.trim(),
      extra_price: Number(
        flavor.extra_price || 0,
      ).toFixed(2),
      price_mode: flavor.price_mode || "flat",
      is_seasonal: flavor.is_seasonal,
      available_from:
        flavor.is_seasonal && flavor.available_from
          ? flavor.available_from
          : null,
      available_until:
        flavor.is_seasonal && flavor.available_until
          ? flavor.available_until
          : null,
      is_active: flavor.is_active,
      is_available: flavor.is_available,
      display_order: index + 1,
    }));

    const add_ons = formData.add_ons.map((addOn) => ({
      ...(addOn.id ? { id: addOn.id } : {}),
      name: addOn.name.trim(),
      description: addOn.description.trim(),
      extra_price: Number(
        addOn.extra_price || 0,
      ).toFixed(2),
      price_mode: addOn.price_mode || "flat",
      is_active: addOn.is_active,
      is_available: addOn.is_available,
      display_order: Number(
        addOn.display_order || 0,
      ),
    }));

    try {
      await onSubmit({
        menuItemFormData: payload,
        variants,
        flavors,
        add_ons,
      });
    } catch (error) {
      console.error("Menu save failed:", error);

      if (
        error.data &&
        typeof error.data === "object"
      ) {
        setErrors(error.data);
      }
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="admin-modal-backdrop">
      <section
        className="admin-menu-modal admin-menu-modal--large"
        role="dialog"
        aria-modal="true"
        aria-labelledby="menu-form-title"
      >
        <header className="admin-menu-modal__header">
          <div>
            <p className="eyebrow">
              {isEditing ? "Edit product" : "New product"}
            </p>

            <h2 id="menu-form-title">
              {isEditing
                ? "Update menu item"
                : "Add a menu item"}
            </h2>
          </div>

          <button
            type="button"
            className="admin-menu-modal__close"
            aria-label="Close form"
            onClick={onClose}
            disabled={isSaving}
          >
            <X size={21} />
          </button>
        </header>

        <form
          className="admin-menu-form"
          onSubmit={handleSubmit}
        >
          <div className="admin-menu-form__scroll">
            <div className="admin-menu-form__image-section">
              <button
                type="button"
                className="admin-menu-image-picker"
                onClick={() =>
                  fileInputRef.current?.click()
                }
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Menu item preview"
                  />
                ) : (
                  <>
                    <ImagePlus size={28} />
                    <strong>Add product photo</strong>
                    <span>
                      PNG, JPG or WEBP up to 5 MB
                    </span>
                  </>
                )}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                onChange={handleImageChange}
              />

              {errors.image && (
                <span className="form-error">
                  {errors.image}
                </span>
              )}
            </div>

            <div className="admin-menu-form__grid">
              <div className="form-group admin-menu-form__wide">
                <label
                  className="form-label"
                  htmlFor="name"
                >
                  Product name
                </label>

                <input
                  id="name"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Cupcakes"
                />

                {errors.name && (
                  <span className="form-error">
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label
                  className="form-label"
                  htmlFor="category"
                >
                  Category
                </label>

                <select
                  id="category"
                  name="category"
                  className="form-control"
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value="">
                    Choose a category
                  </option>

                  {categories.map((category) => (
                    <option
                      key={category.id}
                      value={category.id}
                    >
                      {category.name}
                    </option>
                  ))}
                </select>

                {errors.category && (
                  <span className="form-error">
                    {errors.category}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label
                  className="form-label"
                  htmlFor="display_order"
                >
                  Display order
                </label>

                <input
                  id="display_order"
                  name="display_order"
                  type="number"
                  min="0"
                  className="form-control"
                  value={formData.display_order}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group admin-menu-form__wide">
                <label
                  className="form-label"
                  htmlFor="short_description"
                >
                  Short description
                </label>

                <input
                  id="short_description"
                  name="short_description"
                  className="form-control"
                  maxLength={255}
                  value={formData.short_description}
                  onChange={handleChange}
                  placeholder="Shown on menu cards."
                />

                <span className="form-help">
                  {formData.short_description.length}/255
                </span>
              </div>

              <div className="form-group admin-menu-form__wide">
                <label
                  className="form-label"
                  htmlFor="description"
                >
                  Full description
                </label>

                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the product and ordering details."
                />
              </div>
            </div>

            <section className="admin-menu-form-section">
              <div className="admin-menu-form-section__header">
                <div>
                  <h3>Sizes and prices</h3>
                  <p>
                    Add each purchasable option for this product.
                  </p>
                </div>

                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={addVariant}
                >
                  <Plus size={16} />
                  Add Option
                </button>
              </div>

              {errors.variants && (
                <span className="form-error">
                  {errors.variants}
                </span>
              )}

              <div className="admin-nested-list">
                {formData.variants.map(
                  (variant, index) => (
                    <div
                      className="admin-nested-card"
                      key={variant.id || `variant-${index}`}
                    >
                      <div className="admin-nested-card__header">
                        <strong>
                          Option {index + 1}
                        </strong>

                        {formData.variants.length > 1 && (
                          <button
                            type="button"
                            className="admin-icon-button admin-icon-button--danger"
                            onClick={() =>
                              removeVariant(index)
                            }
                            aria-label="Remove option"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      <div className="admin-menu-form__grid">
                        <div className="form-group">
                          <label className="form-label">
                            Option name
                          </label>

                          <input
                            className="form-control"
                            value={variant.name}
                            onChange={(event) =>
                              updateVariant(
                                index,
                                "name",
                                event.target.value,
                              )
                            }
                            placeholder="Half Dozen"
                          />

                          {errors[
                            `variant_${index}_name`
                          ] && (
                            <span className="form-error">
                              {
                                errors[
                                  `variant_${index}_name`
                                ]
                              }
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Price
                          </label>

                          <div className="admin-price-input">
                            <span>$</span>

                            <input
                              className="form-control"
                              type="number"
                              min="0"
                              step="0.01"
                              value={variant.price}
                              onChange={(event) =>
                                updateVariant(
                                  index,
                                  "price",
                                  event.target.value,
                                )
                              }
                              placeholder="36.00"
                            />
                          </div>

                          {errors[
                            `variant_${index}_price`
                          ] && (
                            <span className="form-error">
                              {
                                errors[
                                  `variant_${index}_price`
                                ]
                              }
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Quantity included
                          </label>

                          <input
                            className="form-control"
                            type="number"
                            min="1"
                            value={variant.quantity}
                            onChange={(event) =>
                              updateVariant(
                                index,
                                "quantity",
                                event.target.value,
                              )
                            }
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">
                            Display order
                          </label>

                          <input
                            className="form-control"
                            type="number"
                            min="0"
                            value={variant.display_order}
                            onChange={(event) =>
                              updateVariant(
                                index,
                                "display_order",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="admin-inline-toggles">
                        <label>
                          <input
                            type="checkbox"
                            checked={variant.is_active}
                            onChange={(event) =>
                              updateVariant(
                                index,
                                "is_active",
                                event.target.checked,
                              )
                            }
                          />
                          Active
                        </label>

                        <label>
                          <input
                            type="checkbox"
                            checked={
                              variant.is_available
                            }
                            onChange={(event) =>
                              updateVariant(
                                index,
                                "is_available",
                                event.target.checked,
                              )
                            }
                          />
                          Available
                        </label>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </section>

            <div className="admin-menu-form__toggles">
              <label className="admin-toggle-row">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />

                <span>
                  <strong>Visible on menu</strong>
                  <small>
                    Customers can see this product.
                  </small>
                </span>
              </label>

              <label className="admin-toggle-row">
                <input
                  type="checkbox"
                  name="is_available"
                  checked={formData.is_available}
                  onChange={handleChange}
                />

                <span>
                  <strong>Available to order</strong>
                  <small>
                    Turn off ordering without hiding the product.
                  </small>
                </span>
              </label>

              <label className="admin-toggle-row">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleChange}
                />

                <span>
                  <strong>Featured item</strong>
                  <small>
                    Highlight this product on the storefront.
                  </small>
                </span>
              </label>

              <label className="admin-toggle-row">
                <input
                  type="checkbox"
                  name="requires_flavor"
                  checked={formData.requires_flavor}
                  onChange={handleChange}
                />

                <span>
                  <strong>Requires a flavor</strong>
                  <small>
                    Customers must select or enter a flavor.
                  </small>
                </span>
              </label>
            </div>

            {formData.requires_flavor && (
              <section className="admin-menu-form-section">
                <div className="admin-menu-form-section__header">
                  <div>
                    <h3>Flavors</h3>
                    <p>
                      Add the flavor choices available for this product.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={addFlavor}
                  >
                    <Plus size={16} />
                    Add Flavor
                  </button>
                </div>

                {errors.flavors && (
                  <span className="form-error">
                    {errors.flavors}
                  </span>
                )}

                <label className="admin-toggle-row">
                  <input
                    type="checkbox"
                    name="allows_custom_flavor"
                    checked={
                      formData.allows_custom_flavor
                    }
                    onChange={handleChange}
                  />

                  <span>
                    <strong>
                      Allow custom flavor requests
                    </strong>
                    <small>
                      Customers may type a flavor not listed below.
                    </small>
                  </span>
                </label>

                {formData.allows_custom_flavor && (
                  <div className="form-group">
                    <label
                      className="form-label"
                      htmlFor="custom_flavor_prompt"
                    >
                      Custom flavor prompt
                    </label>

                    <input
                      id="custom_flavor_prompt"
                      name="custom_flavor_prompt"
                      className="form-control"
                      value={
                        formData.custom_flavor_prompt
                      }
                      onChange={handleChange}
                    />

                    {errors.custom_flavor_prompt && (
                      <span className="form-error">
                        {errors.custom_flavor_prompt}
                      </span>
                    )}
                  </div>
                )}

                <div className="admin-nested-list">
                  {formData.flavors.map(
                    (flavor, index) => (
                      <div
                        className="admin-nested-card"
                        key={
                          flavor.id ||
                          flavor.client_id ||
                          `flavor-${index}`
                        }
                      >
                        <div className="admin-nested-card__header">
                          <strong>
                            Flavor {index + 1}
                            {!flavor.id && (
                              <span className="admin-new-badge">
                                New
                              </span>
                            )}
                          </strong>

                          <button
                            type="button"
                            className="admin-icon-button admin-icon-button--danger"
                            onClick={() =>
                              removeFlavor(index)
                            }
                            aria-label="Remove flavor"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="admin-menu-form__grid">
                          <div className="form-group">
                            <label className="form-label">
                              Flavor name
                            </label>

                            <input
                              ref={
                                !flavor.id && index === 0
                                  ? newFlavorInputRef
                                  : null
                              }
                              className="form-control"
                              value={flavor.name}
                              onChange={(event) =>
                                updateFlavor(
                                  index,
                                  "name",
                                  event.target.value,
                                )
                              }
                              placeholder="Vanilla Bean"
                            />

                            {errors[
                              `flavor_${index}_name`
                            ] && (
                              <span className="form-error">
                                {
                                  errors[
                                    `flavor_${index}_name`
                                  ]
                                }
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Extra price
                            </label>

                            <div className="admin-price-input">
                              <span>$</span>

                              <input
                                className="form-control"
                                type="number"
                                min="0"
                                step="0.01"
                                value={
                                  flavor.extra_price
                                }
                                onChange={(event) =>
                                  updateFlavor(
                                    index,
                                    "extra_price",
                                    event.target.value,
                                  )
                                }
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Extra price applies
                            </label>

                            <select
                              className="form-control"
                              value={flavor.price_mode || "flat"}
                              onChange={(event) =>
                                updateFlavor(
                                  index,
                                  "price_mode",
                                  event.target.value,
                                )
                              }
                            >
                              <option value="flat">
                                Once per selection
                              </option>
                              <option value="per_item">
                                Per individual item
                              </option>
                            </select>

                            <span className="form-help">
                              Use “per individual item” when the
                              surcharge should multiply by the selected
                              quantity.
                            </span>
                          </div>

                          <div className="form-group admin-menu-form__wide">
                            <label className="form-label">
                              Description
                            </label>

                            <input
                              className="form-control"
                              value={
                                flavor.description
                              }
                              onChange={(event) =>
                                updateFlavor(
                                  index,
                                  "description",
                                  event.target.value,
                                )
                              }
                              placeholder="Optional flavor notes."
                            />
                          </div>
                        </div>

                        <div className="admin-inline-toggles">
                          <label>
                            <input
                              type="checkbox"
                              checked={flavor.is_active}
                              onChange={(event) =>
                                updateFlavor(
                                  index,
                                  "is_active",
                                  event.target.checked,
                                )
                              }
                            />
                            Active
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={
                                flavor.is_available
                              }
                              onChange={(event) =>
                                updateFlavor(
                                  index,
                                  "is_available",
                                  event.target.checked,
                                )
                              }
                            />
                            Available
                          </label>

                          <label>
                            <input
                              type="checkbox"
                              checked={
                                flavor.is_seasonal
                              }
                              onChange={(event) =>
                                updateFlavor(
                                  index,
                                  "is_seasonal",
                                  event.target.checked,
                                )
                              }
                            />
                            Seasonal
                          </label>
                        </div>

                        {flavor.is_seasonal && (
                          <div className="admin-menu-form__grid">
                            <div className="form-group">
                              <label className="form-label">
                                Available from
                              </label>

                              <input
                                className="form-control"
                                type="date"
                                value={
                                  flavor.available_from
                                }
                                onChange={(event) =>
                                  updateFlavor(
                                    index,
                                    "available_from",
                                    event.target.value,
                                  )
                                }
                              />
                            </div>

                            <div className="form-group">
                              <label className="form-label">
                                Available until
                              </label>

                              <input
                                className="form-control"
                                type="date"
                                value={
                                  flavor.available_until
                                }
                                onChange={(event) =>
                                  updateFlavor(
                                    index,
                                    "available_until",
                                    event.target.value,
                                  )
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ),
                  )}
                </div>
              </section>
            )}

            <section className="admin-menu-form-section">
              <div className="admin-menu-form-section__header">
                <div>
                  <h3>Add-ons</h3>
                  <p>
                    Manage optional surcharges separately from flavors.
                  </p>
                </div>

                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={addAddOn}
                >
                  <Plus size={16} />
                  Add Add-on
                </button>
              </div>

              <div className="admin-nested-list">
                {formData.add_ons.map((addOn, index) => (
                  <div
                    className="admin-nested-card"
                    key={addOn.id || `add-on-${index}`}
                  >
                    <div className="admin-nested-card__header">
                      <strong>Add-on {index + 1}</strong>

                      <button
                        type="button"
                        className="admin-icon-button admin-icon-button--danger"
                        onClick={() => removeAddOn(index)}
                        aria-label="Remove add-on"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="admin-menu-form__grid">
                      <div className="form-group">
                        <label className="form-label">
                          Add-on name
                        </label>

                        <input
                          className="form-control"
                          value={addOn.name}
                          onChange={(event) =>
                            updateAddOn(
                              index,
                              "name",
                              event.target.value,
                            )
                          }
                          placeholder="Gluten-free"
                        />

                        {errors[`add_on_${index}_name`] && (
                          <span className="form-error">
                            {errors[`add_on_${index}_name`]}
                          </span>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Extra price
                        </label>

                        <div className="admin-price-input">
                          <span>$</span>

                          <input
                            className="form-control"
                            type="number"
                            min="0"
                            step="0.01"
                            value={addOn.extra_price}
                            onChange={(event) =>
                              updateAddOn(
                                index,
                                "extra_price",
                                event.target.value,
                              )
                            }
                          />
                        </div>

                        {errors[`add_on_${index}_extra_price`] && (
                          <span className="form-error">
                            {errors[`add_on_${index}_extra_price`]}
                          </span>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Extra price applies
                        </label>

                        <select
                          className="form-control"
                          value={addOn.price_mode || "flat"}
                          onChange={(event) =>
                            updateAddOn(
                              index,
                              "price_mode",
                              event.target.value,
                            )
                          }
                        >
                          <option value="flat">
                            Once per selection
                          </option>
                          <option value="per_item">
                            Per individual item
                          </option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Display order
                        </label>

                        <input
                          className="form-control"
                          type="number"
                          min="0"
                          value={addOn.display_order}
                          onChange={(event) =>
                            updateAddOn(
                              index,
                              "display_order",
                              event.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="form-group admin-menu-form__wide">
                        <label className="form-label">
                          Description
                        </label>

                        <input
                          className="form-control"
                          value={addOn.description}
                          onChange={(event) =>
                            updateAddOn(
                              index,
                              "description",
                              event.target.value,
                            )
                          }
                          placeholder="Optional add-on notes."
                        />
                      </div>
                    </div>

                    <div className="admin-inline-toggles">
                      <label>
                        <input
                          type="checkbox"
                          checked={addOn.is_active}
                          onChange={(event) =>
                            updateAddOn(
                              index,
                              "is_active",
                              event.target.checked,
                            )
                          }
                        />
                        Active
                      </label>

                      <label>
                        <input
                          type="checkbox"
                          checked={addOn.is_available}
                          onChange={(event) =>
                            updateAddOn(
                              index,
                              "is_available",
                              event.target.checked,
                            )
                          }
                        />
                        Available
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <footer className="admin-menu-modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn btn--primary"
              disabled={isSaving}
            >
              {isSaving && (
                <LoaderCircle
                  size={17}
                  className="admin-spin"
                />
              )}

              {isSaving
                ? "Saving..."
                : isEditing
                  ? "Save Changes"
                  : "Add Menu Item"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

export default MenuItemForm;